import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { PJM_META } from '../../data/aduUtahPjmCanvas';
import InternalBackLink from '../Internal/InternalBackLink';
import useInternalTheme from '../../hooks/useInternalTheme';
import {
  addSubcard,
  createItem,
  deleteItem,
  hydrateFromSeed,
  loadBoard,
  moveItem,
  parseDropTarget,
  renameSubcard,
  saveBoard,
  sectionKeysFor,
  updateItemText,
} from '../../data/aduUtahPjmState';
import '../Internal/internalXlabsChrome.css';
import './InternalAduUtahPjm.css';

/**
 * Internal ADU Utah PJM canvas — docs/INTERNAL_ADU_UTAH_PJM.md
 * Visual system: xlabs chrome (docs/XLABS_HUB.md)
 */

const DND_TYPE = 'application/x-pjm-item';

/** Set while a PJM item handle is dragging (reliable across browsers). */
let activeDragItemId = null;

function readDragItemId(dataTransfer) {
  return (
    activeDragItemId ||
    dataTransfer.getData(DND_TYPE) ||
    dataTransfer.getData('text/plain')
  );
}

function isPjmDrag() {
  return !!activeDragItemId;
}

function EditableText({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (next && next !== value) onChange(next);
    else setDraft(value);
  };

  if (!editing) {
    return (
      <span
        className="pjm-edit"
        onClick={(e) => {
          e.stopPropagation();
          setEditing(true);
        }}
        title="Click to edit"
      >
        {value}
      </span>
    );
  }

  return (
    <textarea
      autoFocus
      value={draft}
      rows={2}
      className="pjm-edit-input"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          setDraft(value);
          setEditing(false);
        }
        e.stopPropagation();
      }}
      onMouseDown={(e) => e.stopPropagation()}
    />
  );
}

function ItemRow({ item, onEdit, onToBank, onDelete, onDragStart, onDragEnd }) {
  return (
    <li className="pjm-item" onDragOver={(e) => e.stopPropagation()}>
      <span
        draggable
        onDragStart={(e) => {
          e.stopPropagation();
          activeDragItemId = item.id;
          e.dataTransfer.setData(DND_TYPE, item.id);
          e.dataTransfer.setData('text/plain', item.id);
          e.dataTransfer.effectAllowed = 'move';
          onDragStart?.(item.id);
        }}
        onDragEnd={() => {
          activeDragItemId = null;
          onDragEnd?.();
        }}
        className="pjm-item__handle"
        title="Drag"
        aria-label="Drag item"
      >
        ⠿
      </span>
      <div className="pjm-item__body">
        <EditableText value={item.text} onChange={onEdit} />
      </div>
      <div className="pjm-item__actions">
        {onToBank ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToBank();
            }}
            title="Move to bank"
          >
            Bank
          </button>
        ) : null}
        <button
          type="button"
          className="is-danger"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          title="Delete permanently"
        >
          Del
        </button>
      </div>
    </li>
  );
}

function ItemList({
  items,
  targetKey,
  onDropItem,
  onEdit,
  onToBank,
  onDelete,
  dropActive,
  setDropActive,
  onDragEnd,
}) {
  const allowDrop = (e) => {
    if (!isPjmDrag()) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropActive(targetKey);
  };

  return (
    <ul
      className={`pjm-list${dropActive === targetKey ? ' is-drop' : ''}`}
      onDragOver={allowDrop}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setDropActive((k) => (k === targetKey ? null : k));
        }
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const itemId = readDragItemId(e.dataTransfer);
        setDropActive(null);
        if (itemId) onDropItem(itemId, targetKey, null);
      }}
    >
      {items.map((item) => (
        <ItemRow
          key={item.id}
          item={item}
          onEdit={(text) => onEdit(item.id, text)}
          onToBank={onToBank ? () => onToBank(item.id) : null}
          onDelete={() => onDelete(item.id)}
          onDragStart={() => setDropActive(null)}
          onDragEnd={onDragEnd}
        />
      ))}
      {!items.length ? (
        <li className="pjm-list__empty">Drop items here</li>
      ) : null}
    </ul>
  );
}

function CollapsibleSection({
  title,
  count,
  open,
  onToggle,
  targetKey,
  onHeaderDrop,
  dropActive,
  setDropActive,
  titleEditable,
  onRename,
  children,
}) {
  return (
    <div
      className={`pjm-sec${dropActive === targetKey ? ' is-drop' : ''}`}
      onDragOver={(e) => {
        if (!isPjmDrag()) return;
        e.preventDefault();
        setDropActive(targetKey);
      }}
      onDrop={(e) => {
        if (!open) {
          e.preventDefault();
          e.stopPropagation();
          const itemId = readDragItemId(e.dataTransfer);
          setDropActive(null);
          if (itemId) onHeaderDrop(itemId, targetKey);
        }
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="pjm-sec__toggle"
      >
        <span className="pjm-sec__title">
          {title}
          {typeof count === 'number' ? (
            <span className="pjm-sec__count">{count}</span>
          ) : null}
        </span>
        <span className={`pjm-sec__chev${open ? ' is-open' : ''}`} aria-hidden>
          ›
        </span>
      </button>
      {open ? (
        <div className="pjm-sec__body">
          {titleEditable && onRename ? (
            <div className="pjm-sec__rename">
              <div className="pjm-sec__rename-lbl">Subcard title</div>
              <EditableText value={title} onChange={onRename} />
            </div>
          ) : null}
          {children}
        </div>
      ) : null}
    </div>
  );
}

function BankColumn({
  bank,
  onCreate,
  onDropItem,
  onEdit,
  onDelete,
  dropActive,
  setDropActive,
  onDragEnd,
}) {
  const [draft, setDraft] = useState('');

  return (
    <aside className="pjm-col pjm-col--bank">
      <header className="pjm-col__hd">
        <div className="xc-eyebrow">Holding area</div>
        <h2>Item bank</h2>
        <p>Create here, then drag into a section. Park unused items back.</p>
      </header>

      <div className="pjm-col__body">
        <form
          className="pjm-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!draft.trim()) return;
            onCreate(draft);
            setDraft('');
          }}
        >
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            placeholder="New item…"
          />
          <button type="submit" className="xc-btn xc-btn--solid">
            Add to bank
          </button>
        </form>

        <div className="pjm-bank-count">Bank {bank.length}</div>

        <ItemList
          items={bank}
          targetKey="bank"
          onDropItem={onDropItem}
          onEdit={onEdit}
          onToBank={null}
          onDelete={onDelete}
          dropActive={dropActive}
          setDropActive={setDropActive}
          onDragEnd={onDragEnd}
        />
      </div>
    </aside>
  );
}

function WorkstreamColumn({
  ws,
  openMap,
  setSection,
  onDropItem,
  onEdit,
  onToBank,
  onDelete,
  onAddSubcard,
  onRenameSubcard,
  dropActive,
  setDropActive,
  onDragEnd,
}) {
  const id = (key) => `${ws.id}::${key}`;

  const sectionProps = (sectionKey, title, items, extra = {}) => ({
    title,
    count: items.length,
    open: !!openMap[id(sectionKey)],
    onToggle: () => setSection(id(sectionKey)),
    targetKey: id(sectionKey),
    onHeaderDrop: (itemId, targetKey) => {
      setSection(id(sectionKey), true);
      onDropItem(itemId, targetKey, null);
    },
    dropActive,
    setDropActive,
    ...extra,
  });

  const listProps = (sectionKey, items) => ({
    items,
    targetKey: id(sectionKey),
    onDropItem,
    onEdit,
    onToBank,
    onDelete,
    dropActive,
    setDropActive,
    onDragEnd,
  });

  return (
    <article className="pjm-col">
      <header className="pjm-col__hd">
        <div className="xc-eyebrow">Workstream {ws.label}</div>
        <h2>{ws.title}</h2>
        <p>{ws.objective}</p>
      </header>

      <div className="pjm-col__body">
        <CollapsibleSection {...sectionProps('knowns', 'Knowns', ws.knowns)}>
          <ItemList {...listProps('knowns', ws.knowns)} />
        </CollapsibleSection>

        <CollapsibleSection {...sectionProps('unknowns', 'Unknowns', ws.unknowns)}>
          <ItemList {...listProps('unknowns', ws.unknowns)} />
        </CollapsibleSection>

        {(ws.subItems || []).map((block) => (
          <CollapsibleSection
            key={block.id}
            {...sectionProps(`sub:${block.id}`, block.title, block.items, {
              titleEditable: true,
              onRename: (title) => onRenameSubcard(ws.id, block.id, title),
            })}
          >
            <ItemList {...listProps(`sub:${block.id}`, block.items)} />
          </CollapsibleSection>
        ))}

        <button
          type="button"
          onClick={() => onAddSubcard(ws.id)}
          className="pjm-add-sub"
        >
          + Add subcard
        </button>

        <CollapsibleSection
          {...sectionProps('considerations', 'Considerations', ws.considerations)}
        >
          <ItemList {...listProps('considerations', ws.considerations)} />
        </CollapsibleSection>

        <CollapsibleSection {...sectionProps('next', 'Next steps', ws.nextSteps)}>
          <ItemList {...listProps('next', ws.nextSteps)} />
        </CollapsibleSection>
      </div>
    </article>
  );
}

export default function InternalAduUtahPjm() {
  const theme = useInternalTheme();
  const [board, setBoard] = useState(() => loadBoard());
  const [openMap, setOpenMap] = useState({});
  const [dropActive, setDropActive] = useState(null);

  useEffect(() => {
    saveBoard(board);
  }, [board]);

  const allKeys = useMemo(
    () =>
      board.workstreams.flatMap((ws) =>
        sectionKeysFor(ws).map((key) => `${ws.id}::${key}`)
      ),
    [board.workstreams]
  );

  const expandAll = allKeys.length > 0 && allKeys.every((k) => openMap[k]);

  const setSection = (key, forceOpen) => {
    setOpenMap((prev) => ({
      ...prev,
      [key]: forceOpen === true ? true : !prev[key],
    }));
  };

  const toggleAll = () => {
    if (expandAll) {
      setOpenMap({});
      return;
    }
    setOpenMap(Object.fromEntries(allKeys.map((k) => [k, true])));
  };

  const apply = (updater) => {
    setBoard((prev) => updater(prev));
  };

  const clearDrop = () => setDropActive(null);

  const handleDropItem = (itemId, targetKey, beforeItemId) => {
    const toLoc = parseDropTarget(targetKey);
    if (!toLoc) return;
    apply((b) => moveItem(b, itemId, toLoc, beforeItemId));
    clearDrop();
  };

  const handleToBank = (itemId) => {
    apply((b) => moveItem(b, itemId, { kind: 'bank' }, null));
  };

  const handleDelete = (itemId) => {
    if (!window.confirm('Delete this item permanently?')) return;
    apply((b) => deleteItem(b, itemId));
  };

  const handleReset = () => {
    if (!window.confirm('Reset board to seed data? This clears the bank and local edits.')) {
      return;
    }
    setBoard(hydrateFromSeed());
    setOpenMap({});
  };

  return (
    <div
      className="xlabs-chrome xlabs-pjm"
      data-hub-theme={theme === 'light' ? undefined : theme}
    >
      <Helmet>
        <title>utah · internal</title>
        <meta name="robots" content="noindex,nofollow" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter+Tight:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <div className="pjm-top">
        <div className="pjm-top__inner">
          <div>
            <p className="xc-eyebrow">
              <InternalBackLink className="xc-back" />
              <span style={{ margin: '0 8px', color: 'var(--line)' }}>·</span>
              <span>internal · utah</span>
            </p>
            <h1 className="xc-title">{PJM_META.title}</h1>
            <p className="xc-sub">
              {PJM_META.subtitle}. {PJM_META.northStar}
            </p>
            <p className="pjm-focus">Call focus: {PJM_META.callFocus}</p>
          </div>
          <div className="pjm-top__actions">
            <button type="button" className="xc-btn" onClick={toggleAll}>
              {expandAll ? 'Collapse all' : 'Expand all'}
            </button>
            <button type="button" className="xc-btn" onClick={handleReset}>
              Reset board
            </button>
            <Link to="/internal" className="xc-btn xc-btn--solid">
              Exit
            </Link>
          </div>
        </div>
      </div>

      <div className="pjm-body">
        <div className="pjm-layout">
          <BankColumn
            bank={board.bank}
            onCreate={(text) => apply((b) => createItem(b, text))}
            onDropItem={handleDropItem}
            onEdit={(itemId, text) => apply((b) => updateItemText(b, itemId, text))}
            onDelete={handleDelete}
            dropActive={dropActive}
            setDropActive={setDropActive}
            onDragEnd={clearDrop}
          />
          <div className="pjm-streams">
            {board.workstreams.map((ws) => (
              <WorkstreamColumn
                key={ws.id}
                ws={ws}
                openMap={openMap}
                setSection={setSection}
                onDropItem={handleDropItem}
                onEdit={(itemId, text) => apply((b) => updateItemText(b, itemId, text))}
                onToBank={handleToBank}
                onDelete={handleDelete}
                onAddSubcard={(wsId) => {
                  let subId = null;
                  setBoard((prev) => {
                    const result = addSubcard(prev, wsId);
                    subId = result.subId;
                    return result.board;
                  });
                  if (subId) {
                    setOpenMap((prev) => ({
                      ...prev,
                      [`${wsId}::sub:${subId}`]: true,
                    }));
                  }
                }}
                onRenameSubcard={(wsId, subId, title) =>
                  apply((b) => renameSubcard(b, wsId, subId, title))
                }
                dropActive={dropActive}
                setDropActive={setDropActive}
                onDragEnd={clearDrop}
              />
            ))}
          </div>
        </div>
        <p className="pjm-foot">
          Drag from the ⠿ handle. Click text to edit. Edits save in this browser.
          {' · '}
          See <code>docs/INTERNAL_ADU_UTAH_PJM.md</code>
        </p>
      </div>
    </div>
  );
}
